// Outbound message constructors. Mirrors app/ws/messages.py.
//
// All wire-format keys live in this single module. The backend's
// `StartMessage` is configured with `extra: "ignore"`, so the optional
// frontend identity fields below are forward-compatible: older backends
// silently drop them, newer backends may surface them in their debug stream.

export function startMessage({
  voice,
  mode,
  thinkingLevel,
  includeThoughts,
  workflowId,
  language,
  frontendName,
  frontendVersion,
  protocolVersion,
} = {}) {
  const out = {
    type: "start",
    voice,
    mode,
    thinking_level: thinkingLevel,
    include_thoughts: includeThoughts,
    workflow_id: workflowId,
  };
  // ``language`` is optional and only attached when supplied. Older backends
  // ignore unknown keys (``extra: "ignore"``); leaving it off when not set
  // preserves the legacy key order asserted by the protocol tests.
  if (language !== undefined && language !== null) out.language = language;
  if (frontendName !== undefined) out.frontend_name = frontendName;
  if (frontendVersion !== undefined) out.frontend_version = frontendVersion;
  if (protocolVersion !== undefined) out.protocol_version = protocolVersion;
  return out;
}

export function textMessage(text) {
  return { type: "text", text };
}

export function imageMessage(frame) {
  return { type: "image", ...frame };
}

// Distinct from imageMessage so the backend cannot accidentally feed this
// frame into the long-running observer Live session. The backend opens a
// fresh clean Live session per scan attempt for this message type.
//
// `frame` provides the encoded JPEG (mime_type, data, source_id, source_kind,
// source_index, width, height, byte_length); `meta` adds workflow_id /
// step_id / attempt that the backend uses to validate the active step.
export function documentScanImageMessage(frame, meta = {}) {
  const { workflowId, stepId, attempt } = meta;
  const out = { type: "document_scan_image", ...frame };
  if (workflowId !== undefined) out.workflow_id = workflowId;
  if (stepId !== undefined) out.step_id = stepId;
  if (attempt !== undefined) out.attempt = attempt;
  return out;
}

// External work context from the how.fm Trainer (SwiftLOG packing screen,
// scanned SLAM item) plus the events the Trainer derived since its last push.
// Only sent for workflows that declare `packing_proof`.
export function contextMessage(context, events = [], source = "howfm-trainer") {
  return {
    type: "context",
    source,
    context: context && typeof context === "object" ? context : {},
    events: Array.isArray(events) ? events.filter((e) => typeof e === "string").slice(0, 16) : [],
  };
}

// One high-resolution crate still for the backend's packaging assessment.
// Distinct from imageMessage for the same reason as documentScanImageMessage:
// it must never reach the observer's realtime video channel.
export function proofImageMessage(frame, meta = {}) {
  const { requestId, purpose, stage, capturedAtMs } = meta;
  const out = { type: "proof_image", mime_type: frame.mime_type, data: frame.data };
  if (frame.source_index !== undefined) out.source_index = frame.source_index;
  if (frame.width) out.width = frame.width;
  if (frame.height) out.height = frame.height;
  if (requestId) out.request_id = requestId;
  if (purpose) out.purpose = purpose;
  if (stage) out.stage = stage;
  if (capturedAtMs) out.captured_at_ms = capturedAtMs;
  return out;
}

export function stopMessage() {
  return { type: "stop" };
}
